// The Memory Web (docs/04 §19, docs/07 §5): pan/zoom node graph on a starfield of blinking eyes.
import { AnimatedSprite, Container, Graphics, Sprite, type Ticker } from 'pixi.js';
import { Viewport } from 'pixi-viewport';
import { PALETTE } from '../../art/palette';
import { getAtlas, textTexture } from '../../art/rasterize';
import { NODES } from '../../content/registry';
import { mainPath, WEB_LAYOUT, webPosition } from '../../content/webLayout';
import { SPRITES_BY_ID } from '../../art/sprites';
import { demoAllows } from '../../platform/demo';
import { nodeStatus, type NodeStatus } from '../../sim/meta';
import type { GameState } from '../../sim/types';
import { setUI, store } from '../../state/store';
import { audio } from '../../audio/audio';
import { LOGICAL_H, LOGICAL_W, type GameApp } from '../app';
import { clock } from '../timeline/clock';

interface NodeView {
  id: string;
  root: Container;
  sprite: Sprite;
  ring: Graphics;
  label: Sprite;
  /** A lock over nodes whose requirements are missing. */
  lock: Graphics;
  /** The "save up for this" badge on the next gate of the main path. */
  badge: Sprite;
  status: NodeStatus;
}

/** The spine of the Web, ROOT → Cathedral: drawn brighter so the way forward reads at a glance. */
const SPINE = mainPath();
const SPINE_EDGES = new Set(SPINE.slice(1).map((id, i) => [SPINE[i]!, id].sort().join('|')));

const WORLD = 2000;

export class WebScene extends Container {
  private viewport: Viewport;
  private edges = new Graphics();
  private nodes = new Map<string, NodeView>();
  private stars: AnimatedSprite[] = [];
  private t = 0;

  constructor(private ga: GameApp) {
    super();
    const bg = new Graphics().rect(0, 0, LOGICAL_W, LOGICAL_H).fill(PALETTE.ink);
    this.addChild(bg);
    this.viewport = new Viewport({
      screenWidth: LOGICAL_W,
      screenHeight: LOGICAL_H,
      worldWidth: WORLD,
      worldHeight: WORLD,
      events: ga.app.renderer.events,
    });
    this.viewport.drag().wheel({ smooth: 4 }).pinch().decelerate({ friction: 0.9 }).clampZoom({ minScale: 0.5, maxScale: 2 });
    this.viewport.clamp({ left: -WORLD / 2, right: WORLD / 2, top: -WORLD / 2, bottom: WORLD / 2, underflow: 'center' });
    this.viewport.moveCenter(0, -20);
    this.addChild(this.viewport);
    this.buildStars();
    this.viewport.addChild(this.edges);
    this.buildNodes();
    ga.app.ticker.add(this.tick);
  }

  private buildStars(): void {
    const a = getAtlas();
    const frames = [a.tex('pixel_eye', 0), a.tex('pixel_eye', 1)];
    let seed = 7;
    const rnd = () => ((seed = (seed * 16807) % 2147483647) / 2147483647);
    for (let i = 0; i < 70; i++) {
      const s = new AnimatedSprite(frames);
      s.anchor.set(0.5);
      s.position.set(Math.round((rnd() - 0.5) * WORLD), Math.round((rnd() - 0.5) * WORLD));
      s.alpha = 0.25 + rnd() * 0.35;
      s.gotoAndStop(0);
      this.stars.push(s);
      this.viewport.addChild(s);
    }
  }

  private buildNodes(): void {
    const a = getAtlas();
    for (const p of WEB_LAYOUT) {
      const def = NODES.get(p.id);
      if (!def) continue;
      const pos = webPosition(p);
      const root = new Container();
      root.position.set(pos.x, pos.y);
      const gate = def.kind !== 'stat';
      const sprite = new Sprite(a.tex(gate ? 'node_gate' : 'node_stat'));
      sprite.anchor.set(0.5);
      const ring = new Graphics();
      const label = new Sprite();
      label.anchor.set(0.5, 0);
      label.position.set(0, gate ? 14 : 10);
      // Owner request (docs/09 Phase 6): every gate its own icon, every stat its branch's icon.
      const iconId = gate ? `gi_${p.id}` : `bi_${def.branch}`;
      const icon = SPRITES_BY_ID.has(iconId) ? new Sprite(a.tex(iconId)) : null;
      icon?.anchor.set(0.5);
      // A dark window behind the icon (the medallions have their own art inside).
      const backing = new Graphics();
      if (icon) {
        const half = gate ? 7 : 5;
        backing.rect(-half, -half, half * 2, half * 2).fill(PALETTE.ink).rect(-half + 1, -half + 1, half * 2 - 2, half * 2 - 2).fill(PALETTE.aubergine);
      }
      const lock = new Graphics();
      const r = gate ? 12 : 8;
      lock.rect(r - 5, -r - 2, 7, 5).fill(PALETTE.ink).rect(r - 4, -r - 1, 5, 3).fill(PALETTE.steel);
      lock.rect(r - 4, -r - 5, 5, 4).stroke({ color: PALETTE.steel, width: 1 });
      const badge = new Sprite(textTexture('!', 'gold', 'ink', 2));
      badge.anchor.set(0.5, 1);
      badge.position.set(0, -r - 3);
      badge.visible = false;
      root.addChild(ring, sprite, backing, ...(icon ? [icon] : []), label, lock, badge);
      root.eventMode = 'static';
      root.cursor = 'pointer';
      root.on('pointertap', () => {
        audio.play('click');
        setUI({ selectedNode: p.id });
      });
      root.on('pointerover', () => audio.play('hover', { volume: 0.3 }));
      this.viewport.addChild(root);
      this.nodes.set(p.id, { id: p.id, root, sprite, ring, label, lock, badge, status: 'hidden' });
    }
  }

  sync(game: GameState): void {
    const meta = game.meta;
    const selected = store.getState().ui.selectedNode;
    // The next key gate: the first gate on the spine you don't own yet (and can see).
    const next = SPINE.find((id) => !(meta.nodes[id] ?? 0) && nodeStatus(meta, id).status !== 'hidden');
    for (const v of this.nodes.values()) {
      const st = demoAllows(v.id) ? nodeStatus(meta, v.id) : { status: 'hidden' as const, cost: Infinity, missing: [] };
      const level = meta.nodes[v.id] ?? 0;
      const def = NODES.get(v.id)!;
      v.status = level > 0 && st.status !== 'maxed' ? (st.status === 'affordable' ? 'affordable' : 'owned') : st.status;
      const visible = v.status !== 'hidden';
      v.root.visible = true;
      v.root.alpha = visible ? 1 : 0.18;
      v.root.eventMode = visible ? 'static' : 'none';
      v.sprite.tint = level > 0 ? 0xffffff : v.status === 'affordable' ? 0xffffff : PALETTE.lilacGrey;
      v.ring.clear();
      const r = def.kind === 'stat' ? 10 : 15;
      const isNext = v.id === next;
      if (v.id === selected) v.ring.rect(-r, -r, r * 2, r * 2).stroke({ color: PALETTE.white, width: 1 });
      else if (isNext) v.ring.rect(-r - 1, -r - 1, r * 2 + 2, r * 2 + 2).stroke({ color: PALETTE.gold, width: 2 });
      else if (v.status === 'affordable') v.ring.rect(-r, -r, r * 2, r * 2).stroke({ color: PALETTE.lavender, width: 1 });
      v.badge.visible = isNext;
      v.lock.visible = v.status === 'locked';
      // owned: level pips; buyable: the cost (bright when affordable)
      let text = '';
      if (visible && level > 0 && def.maxLevel > 1) text = `${level}/${def.maxLevel}`;
      if (visible && st.status !== 'maxed' && st.status !== 'hidden' && (level === 0 || def.maxLevel > 1))
        text = level > 0 ? `${text} ${st.cost}` : `${st.cost}`;
      const color = st.status === 'affordable' ? 'pinkLilac' : level > 0 ? 'lavender' : st.status === 'locked' ? 'red' : 'lilacGrey';
      v.label.texture = text ? textTexture(text, color, 'ink') : textTexture(' ', 'ink', null);
    }
    const g = this.edges.clear();
    for (const p of WEB_LAYOUT) {
      const a = webPosition(p);
      const la = meta.nodes[p.id] ?? 0;
      for (const l of p.links) {
        const q = WEB_LAYOUT.find((x) => x.id === l);
        if (!q) continue;
        const b = webPosition(q);
        const lb = meta.nodes[l] ?? 0;
        const spine = SPINE_EDGES.has([p.id, l].sort().join('|'));
        const color = spine
          ? la > 0 && lb > 0
            ? PALETTE.gold
            : PALETTE.bronze
          : la > 0 && lb > 0
            ? PALETTE.lavender
            : la > 0 || lb > 0
              ? PALETTE.purple
              : PALETTE.night;
        g.moveTo(a.x, a.y).lineTo(b.x, b.y).stroke({ color, width: spine || (la > 0 && lb > 0) ? 2 : 1 });
      }
    }
  }

  private tick = (tk: Ticker) => {
    if (!this.visible) return;
    this.t += tk.deltaMS;
    for (const v of this.nodes.values()) {
      const base = v.status === 'affordable' ? 1 + 0.12 * Math.max(0, Math.sin(this.t / 180)) : 1;
      v.sprite.scale.set(base);
      // The next gate's badge bobs (a slow 1-px bob, not a flash).
      if (v.badge.visible) v.badge.y = -(v.sprite.height / 2) - 3 - Math.round(Math.max(0, Math.sin(this.t / 300)) * 2);
    }
    if (Math.random() < 0.05) {
      const s = this.stars[Math.floor(Math.random() * this.stars.length)];
      if (s) {
        s.gotoAndStop(1);
        setTimeout(() => s.gotoAndStop(0), 140);
      }
    }
  };

  async nodeBought(id: string): Promise<void> {
    const v = this.nodes.get(id);
    if (!v) return;
    this.sync(store.getState().game);
    const flash = new Graphics().rect(-16, -16, 32, 32).fill(PALETTE.white);
    v.root.addChild(flash);
    await clock.tween(flash, 400, { alpha: 0 });
    flash.destroy();
    v.sprite.scale.set(1.6);
    await clock.tween(v.sprite.scale, 250, { x: 1, y: 1, ease: 'back.out(2)' });
  }

  focus(id: string): void {
    const p = WEB_LAYOUT.find((x) => x.id === id);
    if (!p) return;
    const pos = webPosition(p);
    this.viewport.animate({ position: { x: pos.x, y: pos.y }, time: 300, ease: 'easeOutQuad' });
  }

  resetView(): void {
    this.viewport.setZoom(1, true);
    this.viewport.moveCenter(0, -20);
  }
}
